import { useState, useEffect, useCallback, useRef } from 'react';
import { Space, SpaceList, apiService } from '../services/api';
import { apiRequestManager } from '../services/apiRequestManager';
import { dashboardCache } from '../services/dashboardCache';

interface UseSpacesQueryResult {
  spaces: Space[];
  totalCount: number;
  spaceLimit: number;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

interface UseSpaceQueryResult {
  space: Space | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

interface UseCreateSpaceMutationResult {
  mutate: (spaceData: { name: string; icon?: string; color?: string; template_type?: string }) => Promise<Space>;
  isPending: boolean;
  error: Error | null;
}

interface UseUpdateSpaceMutationResult {
  mutate: (spaceId: string, updates: { name?: string; icon?: string; color?: string }) => Promise<Space>;
  isPending: boolean;
  error: Error | null;
}

interface UseDeleteSpaceMutationResult {
  mutate: (spaceId: string) => Promise<void>;
  isPending: boolean;
  error: Error | null;
}

// Global cache for spaces data (singleton pattern)
class SpacesCache {
  private static instance: SpacesCache;
  private cache: SpaceList | null = null;
  private lastFetch: number = 0;
  private readonly STALE_TIME = 30000; // 30 seconds
  private subscribers = new Set<() => void>();
  private isFetching = false;
  private fetchPromise: Promise<SpaceList> | null = null;
  /** Bumped on invalidate so an in-flight list cannot refill a cleared cache. */
  private generation = 0;

  static getInstance(): SpacesCache {
    if (!SpacesCache.instance) {
      SpacesCache.instance = new SpacesCache();
    }
    return SpacesCache.instance;
  }

  subscribe(callback: () => void): () => void {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }

  private notify(): void {
    this.subscribers.forEach(callback => callback());
  }

  getCache(): SpaceList | null {
    return this.cache;
  }

  isStale(): boolean {
    if (!this.cache) return true;
    return Date.now() - this.lastFetch > this.STALE_TIME;
  }

  async fetch(force = false): Promise<SpaceList> {
    // Return cached data if fresh and not forcing
    if (!force && this.cache && !this.isStale()) {
      return this.cache;
    }

    // If already fetching, return the existing promise
    if (this.isFetching && this.fetchPromise) {
      return this.fetchPromise;
    }

    // Key includes generation so a fetch started after invalidate does not
    // join a request that began before the cache was cleared.
    const generation = this.generation;
    this.isFetching = true;
    this.fetchPromise = apiRequestManager.executeRequest(
      `spaces_list_${generation}`,
      () => apiService.getAllSpaces(),
      {
        dedupeDuration: 2000,
        maxRetries: 1,
        baseDelay: 2000
      }
    ).then((data) => {
      if (generation !== this.generation) {
        return data;
      }
      this.cache = data;
      this.lastFetch = Date.now();
      this.isFetching = false;
      this.fetchPromise = null;
      this.notify();
      return data;
    }).catch((error) => {
      if (generation === this.generation) {
        this.isFetching = false;
        this.fetchPromise = null;
      }
      throw error;
    });

    return this.fetchPromise;
  }

  updateCache(updater: (current: SpaceList | null) => SpaceList | null): void {
    const next = updater(this.cache);
    if (next === null) return;
    this.cache = next;
    this.lastFetch = Date.now();
    this.notify();
  }

  invalidate(): void {
    this.generation += 1;
    this.cache = null;
    this.lastFetch = 0;
    this.isFetching = false;
    this.fetchPromise = null;
    this.notify();
  }
}

const spacesCache = SpacesCache.getInstance();

// Export cache instance for use by other services
export { spacesCache };

/**
 * Hook for fetching all spaces with caching and deduplication
 */
export function useSpacesQuery(): UseSpacesQueryResult {
  const [spaces, setSpaces] = useState<Space[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [spaceLimit, setSpaceLimit] = useState(3);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const mountedRef = useRef(true);

  const fetchSpaces = useCallback(async (force = false) => {
    try {
      setIsLoading(true);
      setError(null);

      const data = await spacesCache.fetch(force);
      
      if (!mountedRef.current) return;

      setSpaces(data.spaces);
      setTotalCount(data.total_count);
      setSpaceLimit(data.space_limit);
    } catch (err: any) {
      if (!mountedRef.current) return;
      
      // Handle timeout errors gracefully
      if (err?.code === 'ECONNABORTED' || err?.message?.includes('timeout')) {
        // Preserve existing data on timeout (stale-while-revalidate)
        const cached = spacesCache.getCache();
        if (cached) {
          // Silently use cached data - don't log warning if we have data
          setSpaces(cached.spaces);
          setTotalCount(cached.total_count);
          setSpaceLimit(cached.space_limit);
          // Don't set error for timeout if we have cached data
          return;
        }
        // Only log and set error if no cached data available
        console.warn('⚠️ Spaces API timeout - no cached data available');
        setError(new Error('Request timeout - please check your connection'));
      } else {
        // Only log non-timeout errors
        if (!err?.code || err.code !== 'ECONNABORTED') {
          console.error('Failed to fetch spaces:', err);
        }
        setError(err instanceof Error ? err : new Error('Failed to fetch spaces'));
        
        // Preserve existing data on error (stale-while-revalidate)
        const cached = spacesCache.getCache();
        if (cached) {
          setSpaces(cached.spaces);
          setTotalCount(cached.total_count);
          setSpaceLimit(cached.space_limit);
        }
      }
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    
    // Initial fetch
    fetchSpaces();

    // Subscribe to cache updates
    const unsubscribe = spacesCache.subscribe(() => {
      if (mountedRef.current) {
        const cached = spacesCache.getCache();
        if (cached) {
          setSpaces(cached.spaces);
          setTotalCount(cached.total_count);
          setSpaceLimit(cached.space_limit);
        }
      }
    });

    return () => {
      mountedRef.current = false;
      unsubscribe();
    };
  }, [fetchSpaces]);

  const refetch = useCallback(() => fetchSpaces(true), [fetchSpaces]);

  return {
    spaces,
    totalCount,
    spaceLimit,
    isLoading,
    error,
    refetch
  };
}

/**
 * Hook for fetching a single space by ID
 */
export function useSpaceQuery(spaceId: string | null): UseSpaceQueryResult {
  const [space, setSpace] = useState<Space | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const mountedRef = useRef(true);

  const fetchSpace = useCallback(async () => {
    if (!spaceId) {
      setSpace(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // First check cache
      const cached = spacesCache.getCache();
      const cachedSpace = cached?.spaces.find(s => s.space_id === spaceId);
      
      if (cachedSpace && !spacesCache.isStale()) {
        if (mountedRef.current) {
          setSpace(cachedSpace);
          setIsLoading(false);
        }
        return;
      }

      // Fetch from API if not in cache or stale
      const spaceData = await apiService.getSpace(spaceId);
      
      if (!mountedRef.current) return;

      setSpace(spaceData);
    } catch (err: any) {
      if (!mountedRef.current) return;
      
      console.error('Failed to fetch space:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch space'));
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [spaceId]);

  useEffect(() => {
    mountedRef.current = true;
    fetchSpace();

    return () => {
      mountedRef.current = false;
    };
  }, [fetchSpace]);

  const refetch = useCallback(() => fetchSpace(), [fetchSpace]);

  return {
    space,
    isLoading,
    error,
    refetch
  };
}

/**
 * Hook for creating a space with optimistic updates
 */
export function useCreateSpaceMutation(): UseCreateSpaceMutationResult {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(async (spaceData: { name: string; icon?: string; color?: string; template_type?: string }): Promise<Space> => {
    setIsPending(true);
    setError(null);

    try {
      // Optimistic update: add temporary space to cache
      const tempId = `temp-${Date.now()}`;
      const tempSpace: Space = {
        id: tempId,
        space_id: tempId,
        name: spaceData.name,
        icon: spaceData.icon || '📁',
        color: spaceData.color || '#8B5CF6',
        template_type: spaceData.template_type || 'custom',
        privacy_level: 'personal',
        role: 'owner',
        memory_count: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      spacesCache.updateCache((current) => {
        if (!current) {
          return {
            spaces: [tempSpace],
            total_count: 1,
            space_limit: 3
          };
        }
        return {
          ...current,
          spaces: [tempSpace, ...current.spaces],
          total_count: current.total_count + 1
        };
      });

      // Create space via API
      const newSpace = await apiService.createSpace({
        name: spaceData.name,
        icon: spaceData.icon || '📁',
        color: spaceData.color || '#8B5CF6',
        template_type: spaceData.template_type || 'custom',
        privacy_level: 'personal'
      });

      // Replace temp space with real one
      spacesCache.updateCache((current) => {
        if (!current) return null;
        return {
          ...current,
          spaces: current.spaces.map(s => s.space_id === tempSpace.space_id ? newSpace : s)
        };
      });

      // Invalidate dashboard cache
      dashboardCache.onSpaceChange();

      // Refetch to ensure consistency
      await spacesCache.fetch(true);

      return newSpace;
    } catch (err: any) {
      console.error('Failed to create space:', err);
      setError(err instanceof Error ? err : new Error('Failed to create space'));
      
      // Rollback optimistic update
      spacesCache.invalidate();
      await spacesCache.fetch(true);
      
      throw err;
    } finally {
      setIsPending(false);
    }
  }, []);

  return {
    mutate,
    isPending,
    error
  };
}

/**
 * Hook for updating a space with optimistic updates
 */
export function useUpdateSpaceMutation(): UseUpdateSpaceMutationResult {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(async (spaceId: string, updates: { name?: string; icon?: string; color?: string }): Promise<Space> => {
    setIsPending(true);
    setError(null);

    try {
      // Optimistic update
      const cached = spacesCache.getCache();
      const originalSpace = cached?.spaces.find(s => s.space_id === spaceId);
      
      if (originalSpace) {
        spacesCache.updateCache((current) => {
          if (!current) return null;
          return {
            ...current,
            spaces: current.spaces.map(s => 
              s.space_id === spaceId 
                ? { ...s, ...updates, updated_at: new Date().toISOString() }
                : s
            )
          };
        });
      }

      // Update via API
      const updatedSpace = await apiService.updateSpace(spaceId, updates);

      // Update cache with real data
      spacesCache.updateCache((current) => {
        if (!current) return null;
        return {
          ...current,
          spaces: current.spaces.map(s => s.space_id === spaceId ? updatedSpace : s)
        };
      });

      // Refetch to ensure consistency
      await spacesCache.fetch(true);

      return updatedSpace;
    } catch (err: any) {
      console.error('Failed to update space:', err);
      setError(err instanceof Error ? err : new Error('Failed to update space'));
      
      // Rollback optimistic update
      spacesCache.invalidate();
      await spacesCache.fetch(true);
      
      throw err;
    } finally {
      setIsPending(false);
    }
  }, []);

  return {
    mutate,
    isPending,
    error
  };
}

/**
 * Hook for deleting a space with optimistic updates
 */
export function useDeleteSpaceMutation(): UseDeleteSpaceMutationResult {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(async (spaceId: string): Promise<void> => {
    setIsPending(true);
    setError(null);

    try {
      // Optimistic update: remove from cache
      const cached = spacesCache.getCache();
      const deletedSpace = cached?.spaces.find(s => s.space_id === spaceId);
      
      if (deletedSpace) {
        spacesCache.updateCache((current) => {
          if (!current) return null;
          return {
            ...current,
            spaces: current.spaces.filter(s => s.space_id !== spaceId),
            total_count: Math.max(0, current.total_count - 1)
          };
        });
      }

      // Delete via API
      await apiService.deleteSpace(spaceId, true);

      // Invalidate dashboard cache
      dashboardCache.onSpaceChange();

      // Refetch to ensure consistency
      await spacesCache.fetch(true);
    } catch (err: any) {
      console.error('Failed to delete space:', err);
      setError(err instanceof Error ? err : new Error('Failed to delete space'));
      
      // Rollback optimistic update
      spacesCache.invalidate();
      await spacesCache.fetch(true);
      
      throw err;
    } finally {
      setIsPending(false);
    }
  }, []);

  return {
    mutate,
    isPending,
    error
  };
}

